import { Build, Component, type ComponentInterface, Element, Event, type EventEmitter, Prop, State, Watch, h } from '@stencil/core';
import type { LtcInputTextCustomEvent } from '~/components';
import type { IIpInfo } from '~/interfaces';
import { ZIP_CODE_REG } from '~/util/constants';
import { CheckZipOnline } from '~/util/leadbox';
import { getIpInfo } from '~/util/lendingtree';

@Component({
  tag: 'ltc-input-zip',
  styleUrl: 'input-zip.less',
  shadow: true,
})
export class LtcInputZip implements ComponentInterface {
  /*
   * 1. Own Properties
   */
  zipEl: HTMLLtcInputTextElement;
  DEFAULT_ZIP: string;
  ZIP_LENGTH = 5 as const;

  /*
   * 2. Reference to host HTML element.
   */
  @Element() host: HTMLElement;

  /*
   * 3. State() variables
   */
  @State() validating = false;
  @State() zipCode = '';

  /*
   * 4. Public Property API
   */
  /**
   * To mark this input as required
   */
  @Prop({ reflect: true }) required: boolean;

  /*
   * Prop lifecycle events
   */
  @Watch('validating')
  doOnValidating(theValue: boolean) {
    this.ltValidating.emit(theValue);
  }

  @Watch('zipCode')
  async doOnZipCode(theValue: string) {
    await this.zipEl.setValue?.(theValue);
    this.ltChange.emit(theValue);
  }

  /*
   * 5. Events section
   */
  /**
   * Fires on ZIP value change
   */
  @Event() ltChange!: EventEmitter<string>;
  /**
   * Fires on ZIP online validating start and end
   */
  @Event() ltValidating!: EventEmitter<boolean>;

  /*
   * 6. Component lifecycle events
   */
  componentWillLoad(): void {
    if (Build.isServer) {
      return;
    }

    getIpInfo().then((ipInfo: IIpInfo) => {
      if (ipInfo?.PostalCode) {
        this.DEFAULT_ZIP = ipInfo.PostalCode;
        this.zipCode = this.DEFAULT_ZIP;
      }
    });
  }

  /*
   * 7. Listeners
   */

  /*
   * 8. Public methods API
   */

  /*
   * 9. Local methods
   */

  private onZipChange = async ({ detail: userInputZip }: LtcInputTextCustomEvent<string>) => {
    if (userInputZip?.length < this.ZIP_LENGTH) {
      return;
    }

    this.validating = true;

    this.zipCode = await CheckZipOnline(userInputZip, this.DEFAULT_ZIP);

    this.validating = false;
  };

  /*
   * 10. render() function
   */
  render() {
    return (
      <ltc-input-text
        ref={(el?: HTMLLtcInputTextElement) => {
          this.zipEl = el as HTMLLtcInputTextElement;
        }}
        label="ZIP Code"
        value={this.zipCode}
        required={this.required}
        disabled={this.validating}
        inputmode="numeric"
        iMaskOptions={{
          mask: ZIP_CODE_REG,
        }}
        onLtcChange={this.onZipChange}
      >
        <ltc-icon name="map" aria-label="ZIP code" slot="leftIcon" />
      </ltc-input-text>
    );
  }
}
